import { GetMetricDataCommand, MetricDataQuery } from '@aws-sdk/client-cloudwatch';
import { getCloudWatchClient } from 'src/services/aws/CloudWatchMetricsService';
import { ENVIRONMENT_VARIABLES } from 'src/constants/EnvironmentConstants';
import { logger } from 'src/logger';

/**
 * Shared CloudWatch dashboard metric extraction.
 *
 * Generalized out of `oe-report/hooks/useKpiMetricsData.ts` so any consumer can pull
 * the numeric values behind a CloudWatch dashboard's `singleValue` widgets without
 * re-implementing the GetMetricData plumbing. `useKpiMetricsData` keeps its
 * domain-specific row shaping (API / Database / User Activity) and now sources the raw
 * per-widget numbers from here; the KPI Metrics page uses `extractSingleValueWidgets`
 * to feed a dashboard-agnostic AI narrative.
 *
 * Only `singleValue` widgets are read — they carry the high-signal aggregate numbers
 * (totals, averages, P90s) that summarize a dashboard. Time-series graph widgets are
 * intentionally out of scope.
 */

const DEFAULT_REGION = ENVIRONMENT_VARIABLES.env.Region || 'us-west-2';

/** One resolved metric line from a `singleValue` widget. */
export interface SingleValueResult {
  /** The CloudWatch result label (e.g. "FinSuite Availability"). */
  label: string;
  /** Sum of all data points in the range — the right aggregate for count-like metrics. */
  sum: number;
  /** Mean of all data points — the right aggregate for rate/percent/latency metrics. */
  avg: number;
  /** Number of data points that contributed, for callers that need to weight results. */
  count: number;
}

/** A `singleValue` widget's title paired with its resolved metric lines. */
export interface SingleValueWidgetGroup {
  /** The widget's configured title (e.g. "API Availability"). */
  title: string;
  /** Resolved metric lines for the widget. */
  entries: SingleValueResult[];
}

/**
 * Computes the full time-range period for singleValue widget queries.
 * Must be a valid CloudWatch period:
 * - Divisible by 60 for periods ≤ 86400
 * - Divisible by 86400 for periods > 86400
 * This replicates `setPeriodToTimeRange: true` behavior in CloudWatch dashboards.
 */
export const computeWidgetPeriod = (startTime: Date, endTime: Date): number => {
  const rangeSeconds = Math.ceil((endTime.getTime() - startTime.getTime()) / 1000);
  if (rangeSeconds <= 86400) {
    // ≤ 1 day: round up to nearest 60s
    return Math.ceil(rangeSeconds / 60) * 60;
  }
  // > 1 day: round up to nearest multiple of 86400 (CW requirement for large periods)
  return Math.ceil(rangeSeconds / 86400) * 86400;
};

/**
 * Queries a single singleValue widget by sending ALL its metrics + expressions in one GetMetricData call.
 * Within a single widget, all expression references are self-contained.
 * Returns only the VISIBLE results (the top-level per-app values).
 *
 * Respects the widget's `setPeriodToTimeRange` property: when true (default for
 * singleValue widgets), overrides all metric/expression periods to match the full
 * query time range, replicating native CloudWatch dashboard behavior.
 */
export const querySingleValueWidget = async (
  rawMetrics: unknown[][],
  startTime: Date,
  endTime: Date,
  setPeriodToTimeRange = true
): Promise<SingleValueResult[]> => {
  const queries: MetricDataQuery[] = [];
  const queryRegions: string[] = []; // Parallel array tracking each query's target region
  let idCounter = 0;
  const effectivePeriod = setPeriodToTimeRange ? computeWidgetPeriod(startTime, endTime) : undefined;

  for (const metricArr of rawMetrics) {
    if (!Array.isArray(metricArr) || metricArr.length === 0) continue;

    // Expression entry: [{expression, label, id, visible, ...}]
    if (metricArr.length === 1 && typeof metricArr[0] === 'object' && metricArr[0] !== null && 'expression' in metricArr[0]) {
      const expr = metricArr[0] as Record<string, unknown>;
      const id = (expr.id as string) || `e_${idCounter++}`;
      queries.push({
        Id: id,
        Expression: expr.expression as string,
        Label: (expr.label as string) || '',
        Period: effectivePeriod || (expr.period as number) || 3600,
        ReturnData: expr.visible !== false
      });
      queryRegions.push(DEFAULT_REGION);
      continue;
    }

    // Standard metric: [Namespace, MetricName, DimKey1, DimValue1, ..., {options}]
    const lastItem = metricArr[metricArr.length - 1];
    const hasOptions = typeof lastItem === 'object' && lastItem !== null && !Array.isArray(lastItem);
    const options = hasOptions ? (lastItem as Record<string, unknown>) : {};

    if (typeof metricArr[0] !== 'string') continue;

    const namespace = metricArr[0] as string;
    const metricName = metricArr[1] as string;

    const dimEntries = hasOptions ? metricArr.slice(2, -1) : metricArr.slice(2);
    const dimensions: { Name: string; Value: string }[] = [];
    for (let i = 0; i < dimEntries.length; i += 2) {
      if (typeof dimEntries[i] === 'string' && typeof dimEntries[i + 1] === 'string') {
        dimensions.push({ Name: dimEntries[i] as string, Value: dimEntries[i + 1] as string });
      }
    }

    const id = (options.id as string) || `m_${idCounter++}`;
    const stat = (options.stat as string) || 'Average';
    const period = effectivePeriod || (options.period as number) || 3600;
    const visible = options.visible !== false;
    const accountId = options.accountId as string | undefined;
    const region = (options.region as string) || DEFAULT_REGION;

    queries.push({
      Id: id,
      MetricStat: {
        Metric: {
          Namespace: namespace,
          MetricName: metricName,
          Dimensions: dimensions
        },
        Period: period,
        Stat: stat
      },
      Label: (options.label as string) || metricName,
      ReturnData: visible,
      AccountId: accountId
    });
    queryRegions.push(region);
  }

  if (queries.length === 0) return [];

  // Build a map of metric ID → region for resolving expression dependencies.
  const idToRegion = new Map<string, string>();
  for (let i = 0; i < queries.length; i++) {
    const q = queries[i];
    if (q.Id && q.MetricStat) idToRegion.set(q.Id, queryRegions[i]);
  }

  // Referenced identifiers in an expression (metric ids, other expression ids, and
  // function names like FILL/IF/SEARCH — the latter simply won't resolve to a region).
  const referencedIdsOf = (expression: string): string[] => expression.match(/\b[a-z][a-z0-9_]*\b/gi) || [];

  // Map every *named* expression id to the ids it references, so an expression that
  // references only other expressions (e.g. a multi-API app whose value is
  // `FILL(m0,0)+FILL(m1,0)`) can still be traced to a concrete metric's region.
  const exprRefs = new Map<string, string[]>();
  for (const q of queries) {
    if (q.Expression && q.Id) exprRefs.set(q.Id, referencedIdsOf(q.Expression));
  }

  // Index queries by id so a visible expression can be grouped with the full transitive
  // closure of ids it references (nested expressions + leaf metrics).
  const queryById = new Map<string, MetricDataQuery>();
  for (const q of queries) if (q.Id) queryById.set(q.Id, q);

  // All regions a given id's transitive closure of *metrics* touches. A single metric
  // yields one region; an expression yields the union of every metric it reaches.
  const regionsForClosure = (startId: string): Set<string> => {
    const regions = new Set<string>();
    const seen = new Set<string>();
    const walk = (id: string): void => {
      if (seen.has(id)) return;
      seen.add(id);
      const direct = idToRegion.get(id);
      if (direct) regions.add(direct);
      for (const ref of exprRefs.get(id) ?? []) walk(ref);
    };
    walk(startId);
    return regions;
  };

  // The full set of query ids a visible expression depends on (itself + everything it
  // transitively references that we actually have a query for), so a cross-region
  // expression can be resolved by fetching exactly its leaf metrics per region.
  const closureIdsOf = (startId: string): string[] => {
    const ids = new Set<string>();
    const walk = (id: string): void => {
      if (ids.has(id)) return;
      if (queryById.has(id)) ids.add(id);
      for (const ref of exprRefs.get(id) ?? []) walk(ref);
    };
    walk(startId);
    return [...ids];
  };

  const results: { key: string; label: string; sum: number; avg: number; count: number }[] = [];
  // Record one resolved line. Mirrors the previous merge-by-region+label behaviour.
  const record = (region: string, label: string, sum: number, count: number): void => {
    if (!label || count <= 0) return;
    const mergeKey = `${region}::${label}`;
    const existing = results.find((e) => e.key === mergeKey);
    if (existing) {
      existing.sum += sum;
      existing.count += count;
      existing.avg = existing.sum / existing.count;
    } else {
      results.push({ key: mergeKey, label, sum, avg: sum / count, count });
    }
  };

  // Send one GetMetricData request (paginated). Returns resolved lines keyed by Id, so a
  // caller can both record visible lines and read raw leaf values for client-side math.
  // Isolated in its own try/catch so one failing request (e.g. a validation error) cannot
  // wipe out the other apps' already-fetched data — the whole-widget wipe was the bug
  // behind the all-dashes "Total Requests"/"5XX Errors" columns.
  const sendOne = async (region: string, reqQueries: MetricDataQuery[]): Promise<Map<string, { label: string; sum: number; count: number }>> => {
    const byId = new Map<string, { label: string; sum: number; count: number }>();
    try {
      let nextToken: string | undefined;
      do {
        const response = await getCloudWatchClient(region).send(
          new GetMetricDataCommand({ StartTime: startTime, EndTime: endTime, MetricDataQueries: reqQueries, NextToken: nextToken })
        );
        for (const r of response.MetricDataResults || []) {
          if (!r.Id || !r.Values || r.Values.length === 0) continue;
          const validValues = r.Values.filter((v) => isFinite(v) && !isNaN(v));
          if (validValues.length === 0) continue;
          const sum = validValues.reduce((s, v) => s + v, 0);
          const prev = byId.get(r.Id);
          if (prev) {
            prev.sum += sum;
            prev.count += validValues.length;
          } else {
            byId.set(r.Id, { label: r.Label || '', sum, count: validValues.length });
          }
        }
        nextToken = response.NextToken;
      } while (nextToken);
    } catch (err) {
      logger.error(`[querySingleValueWidget] GetMetricData failed for region ${region}`, err instanceof Error ? err : new Error(String(err)));
    }
    return byId;
  };

  // Visible queries are the lines the widget actually displays — each becomes one output
  // row. Everything else is a hidden dependency fetched only to resolve a visible line.
  const visibleQueries = queries.filter((q) => q.ReturnData !== false);

  // True when a single expression string is a pure sum of referenced ids, treating a
  // `FILL(ref, <const>)` zero-fill wrapper as equivalent to `ref` (for summation, filling
  // missing datapoints with a constant does not change the sum of real values, and the
  // beta/prod multi-API apps author their roll-ups as `FILL(m0,0) + FILL(m1,0) + ...`).
  // After unwrapping FILL and stripping identifiers, whitespace, parens and `+`, nothing
  // must remain — any other operator or constant (`-`, `*`, `/`, `IF`, bare digits) fails.
  const isPureSumString = (expression: string): boolean => {
    // Only a ZERO fill is unwrapped: for summation, filling missing datapoints with 0 does
    // not change the sum of real values, so the cross-region client-side sum of the raw
    // leaves agrees with what CloudWatch computes for the single-region path. A non-zero
    // fill WOULD change the sum, and the two paths would diverge (the client reconstruction
    // ignores the wrapper), so it must NOT be unwrapped — it fails the pure-sum check and
    // the line is left to the server-side path or skipped.
    const unwrapped = expression.replace(/\bFILL\s*\(\s*([a-z][a-z0-9_]*)\s*,\s*0\s*\)/gi, '$1');
    return unwrapped.replace(/[a-z][a-z0-9_]*/gi, '').replace(/[\s()+]/g, '') === '';
  };

  // True when the ENTIRE transitive expression tree rooted at `startId` is a pure sum —
  // the top expression AND every nested expression it reaches must be pure sums. This is
  // the only form we can safely reconstruct client-side by summing the leaf metrics across
  // regions. A top-level-only check is insufficient: `a + b` where `a` is itself `c * d`
  // would sum `c + d + b`, silently miscomputing the value (AutoSDE finding f-8740b84b).
  const isPureSumClosure = (startId: string): boolean => {
    let sawExpression = false;
    const seen = new Set<string>();
    const walk = (id: string): boolean => {
      if (seen.has(id)) return true;
      seen.add(id);
      const q = queryById.get(id);
      if (!q?.Expression) return true; // a leaf metric (or unknown id) imposes no constraint
      sawExpression = true;
      if (!isPureSumString(q.Expression)) return false;
      for (const ref of exprRefs.get(id) ?? []) if (!walk(ref)) return false;
      return true;
    };
    const ok = walk(startId);
    // Require that we actually saw at least one expression referencing a known query, so a
    // bare/opaque expression (no resolvable refs) is not treated as reconstructable.
    return ok && sawExpression && referencedIdsOf(queryById.get(startId)?.Expression ?? '').some((id) => queryById.has(id));
  };

  // Split the visible lines into those resolvable in a single region (sent in batched
  // per-region calls, as before) and cross-region pure-sum expressions (reconstructed from
  // their leaf metrics). CloudWatch metric math cannot span regions in one GetMetricData
  // call — the stranded-reference ValidationException from that was the all-dashes bug.
  const crossRegionSums: MetricDataQuery[] = [];
  // Per region: the set of query ids (visible lines + their hidden deps) to send together.
  const idsByRegion = new Map<string, Set<string>>();
  // The visible lines to record from each region's response, with their display label.
  const visibleByRegion = new Map<string, { id: string; label: string }[]>();
  const addToRegion = (region: string, ids: string[], visible: { id: string; label: string }): void => {
    if (!idsByRegion.has(region)) idsByRegion.set(region, new Set());
    const set = idsByRegion.get(region)!;
    for (const id of ids) set.add(id);
    if (!visibleByRegion.has(region)) visibleByRegion.set(region, []);
    visibleByRegion.get(region)!.push(visible);
  };

  for (const vq of visibleQueries) {
    const vid = vq.Id as string;
    if (!vq.Expression) {
      const region = idToRegion.get(vid) || DEFAULT_REGION;
      addToRegion(region, [vid], { id: vid, label: vq.Label || '' });
      continue;
    }
    const regions = regionsForClosure(vid);
    if (regions.size <= 1) {
      const region = [...regions][0] || DEFAULT_REGION;
      addToRegion(region, closureIdsOf(vid), { id: vid, label: vq.Label || '' });
    } else if (isPureSumClosure(vid)) {
      crossRegionSums.push(vq);
    } else {
      logger.error(`[querySingleValueWidget] Skipping cross-region expression that is not a fully pure-sum tree: ${vq.Expression}`);
    }
  }

  // One batched GetMetricData call per region for all single-region visible lines. The
  // visible lines return data; their hidden dependencies ride along as ReturnData:false.
  // Each call is isolated (see sendOne) so one region's failure can't wipe the others.
  for (const [region, idSet] of idsByRegion) {
    const reqQueries = [...idSet]
      .map((id) => queryById.get(id))
      .filter((q): q is MetricDataQuery => Boolean(q))
      .map((q) => {
        const isVisibleLine = visibleByRegion.get(region)!.some((v) => v.id === q.Id);
        return { ...q, ReturnData: isVisibleLine };
      });
    const byId = await sendOne(region, reqQueries);
    for (const v of visibleByRegion.get(region)!) {
      const got = byId.get(v.id);
      // Prefer the label CloudWatch returns (matches the pre-existing behaviour); fall
      // back to the query's configured label if the response omitted one.
      if (got) record(region, got.label || v.label, got.sum, got.count);
    }
  }

  // Cross-region pure-sum expressions: fetch each referenced leaf metric in its own region
  // (reusing one batched call per region) and sum the leaves client-side.
  for (const vq of crossRegionSums) {
    const vid = vq.Id as string;
    const leafIds = closureIdsOf(vid).filter((id) => queryById.get(id)?.MetricStat);
    const leavesByRegion = new Map<string, MetricDataQuery[]>();
    for (const id of leafIds) {
      const region = idToRegion.get(id) || DEFAULT_REGION;
      if (!leavesByRegion.has(region)) leavesByRegion.set(region, []);
      leavesByRegion.get(region)!.push({ ...queryById.get(id)!, ReturnData: true });
    }
    let total = 0;
    let totalCount = 0;
    // Every referenced leaf must come back, or the reconstructed sum is incomplete. If any
    // leaf is missing (its region's call failed, or it returned no data), skip the whole
    // app rather than record a misleading partial sum — a partial would understate the
    // real value and look like a genuine low reading.
    let allLeavesResolved = true;
    for (const [region, regionLeaves] of leavesByRegion) {
      const byId = await sendOne(region, regionLeaves);
      for (const leaf of regionLeaves) {
        const got = byId.get(leaf.Id as string);
        if (got) {
          total += got.sum;
          totalCount += got.count;
        } else {
          allLeavesResolved = false;
        }
      }
    }
    const primaryRegion = [...regionsForClosure(vid)][0] || DEFAULT_REGION;
    if (allLeavesResolved && totalCount > 0) record(primaryRegion, vq.Label || '', total, totalCount);
  }

  return results;
};

/**
 * Extracts every `singleValue` widget from a CloudWatch dashboard definition body and
 * resolves its metric values over the given time range. Dashboard-agnostic: it makes no
 * assumptions about metric names or app labels, so it works for any dashboard.
 *
 * Widgets that resolve to no data are omitted, so an empty result means the dashboard
 * has no readable singleValue metrics for the range.
 */
export const extractSingleValueWidgets = async (body: Record<string, unknown>, startTime: Date, endTime: Date): Promise<SingleValueWidgetGroup[]> => {
  const rawWidgets = (body.widgets || []) as Record<string, unknown>[];
  const svWidgets = rawWidgets.filter((rw) => rw.type === 'metric' && (rw.properties as Record<string, unknown>)?.view === 'singleValue');

  const groups: SingleValueWidgetGroup[] = [];
  for (const svWidget of svWidgets) {
    const props = svWidget.properties as Record<string, unknown>;
    const title = (props.title as string) || '';
    const metrics = props.metrics as unknown[][] | undefined;
    if (!metrics) continue;

    const usePeriodOverride = props.setPeriodToTimeRange !== false;
    const entries = await querySingleValueWidget(metrics, startTime, endTime, usePeriodOverride);
    if (entries.length > 0) groups.push({ title, entries });
  }
  return groups;
};
